import { useState, useMemo } from "react";
import {
  Form,
  Input,
  Button,
  Segmented,
  Checkbox,
  ConfigProvider,
  message,
} from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  EditOutlined,
  ExperimentOutlined,
  WalletOutlined,
  LoginOutlined,
  TeamOutlined,
  FireOutlined,
  TableOutlined,
  DeleteOutlined,
  InboxOutlined,
  BellOutlined,
  RiseOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  SettingOutlined, 
} from "@ant-design/icons";
import axios from "../../../utils/axiosDash";
 
/* ---------- Module list ---------- */
export const MODULES = [
  { key: "dashboard", label: "Dashboard", icon: <DashboardOutlined /> },
  { key: "writer", label: "Writer", icon: <EditOutlined /> },
  { key: "hygiene_safety", label: "Hygiene & Safety", icon: <ExperimentOutlined /> },
  { key: "cash_counter", label: "Cash Counter", icon: <WalletOutlined /> },
  { key: "store_entry", label: "Store Entry", icon: <LoginOutlined /> },
  { key: "dining_footfall", label: "Dining Area Footfall", icon: <TeamOutlined /> },
  { key: "kabab_monitoring", label: "Kabab Monitoring", icon: <FireOutlined /> },
  { key: "table_occupancy", label: "Table Occupancy", icon: <TableOutlined /> },
  { key: "wastage_monitoring", label: "Wastage Monitoring", icon: <DeleteOutlined /> },
  { key: "material_inwards", label: "Material Inwards", icon: <InboxOutlined /> },
  { key: "alerts", label: "Alerts", icon: <BellOutlined /> },
  { key: "roi", label: "ROI", icon: <RiseOutlined /> },
  { key: "report", label: "Report", icon: <FileTextOutlined /> },
  { key: "settings", label: "Settings", icon: <SettingOutlined /> },
];
const ALL_KEYS = MODULES.map((m) => m.key);
 
/* Modules that are always selected and can never be deselected */
const REQUIRED_KEYS = ["dashboard"];
 
/* Dashboard is selected by default for both user and admin */
const DEFAULT_KEYS = [...REQUIRED_KEYS];

/* Modules that only admins can have */
const ADMIN_ONLY_KEYS = ["settings"];
 
/* ---------- Password strength helper ---------- */
const getStrength = (pw = "") => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score; // 0 - 4
};
const STRENGTH_META = [
  { label: "Too short", color: "bg-slate-200", text: "text-slate-400" },
  { label: "Weak", color: "bg-rose-300", text: "text-rose-500" },
  { label: "Fair", color: "bg-amber-300", text: "text-amber-600" },
  { label: "Good", color: "bg-sky-400", text: "text-sky-600" },
  { label: "Strong", color: "bg-emerald-400", text: "text-emerald-600" },
];
 
/* ---------- Small layout helpers ---------- */
const SectionCard = ({ icon, title, hint, extra, children }) => (
  <section className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm sm:p-6">
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-lg text-sky-600">
          {icon}
        </span>
        <div>
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          {hint && <p className="text-sm text-slate-500">{hint}</p>}
        </div>
      </div>
      {extra}
    </div>
    {children}
  </section>
);
 
/* ---------- Page ---------- */
export default function AddUser({ onBack, onCreate }) {
  const [form] = Form.useForm();
  const [role, setRole] = useState("user");
  const [selected, setSelected] = useState(DEFAULT_KEYS);
  const [submitting, setSubmitting] = useState(false);
  const [pw, setPw] = useState("");
  const isAdmin = role === "admin";

  // Users don't see admin-only modules (e.g. Settings)
  const visibleModules = useMemo(
    () => (isAdmin ? MODULES : MODULES.filter((m) => !ADMIN_ONLY_KEYS.includes(m.key))),
    [isAdmin]
  );
  const visibleKeys = visibleModules.map((m) => m.key);
  const allChecked = selected.length === visibleKeys.length;
  const strength = useMemo(() => getStrength(pw), [pw]);
  const meta = STRENGTH_META[pw ? Math.max(strength, 1) : 0];
  const user = JSON.parse(localStorage.getItem("user"));
  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};
 
  const handleRoleChange = (value) => {
    setRole(value);
    // Admins always get every module
    if (value === "admin") {
      setSelected(ALL_KEYS);
    } else {
      // User: back to the default (dashboard only); other modules can be added
      setSelected(DEFAULT_KEYS);
    }
  };
 
  const toggleModule = (key) => {
    // Admin gets everything, and required modules (dashboard) can't be removed
    if (isAdmin || REQUIRED_KEYS.includes(key)) return;
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
 
  // "Select all" -> everything; "Unselect all" -> only the required modules
  const toggleAll = (checked) => {
    if (isAdmin) return;
    setSelected(checked ? ALL_KEYS : REQUIRED_KEYS);
  };
 
  const handleReset = () => {
    form.resetFields();
    setRole("user");
    setSelected(DEFAULT_KEYS);
    setPw("");
  };
 
  const handleFinish = async (values) => {
    const orgName = localStorage.getItem("organizationName");
    const organizationId = localStorage.getItem("organizationId");
 
    if (!orgName || !organizationId) {
      message.error("Organization details are missing. Please log in again.");
      return;
    }
 
    // Safety net: dashboard is always included in the saved access list
    const moduleAccess = Array.from(new Set([...REQUIRED_KEYS, ...selected])).filter(
      (key) => isAdmin || !ADMIN_ONLY_KEYS.includes(key)
    );

    const payload = {
      username: values.username.trim(),
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      phone: values.phone,
      password: values.password,
      role,
      // true when Admin is selected, false when User is selected
      empireAdmin: role === "admin",
      moduleAccess,
      orgName,
      organizationId,
    };
 
    try {
      setSubmitting(true);
 
      const response = await axios.post("/add-empire-users", payload);
 
      if (response.data?.success) {
        message.success(
          response.data.message || `${payload.name} was added successfully.`
        );
 
        handleReset();
 
        // Pass the saved user returned by the backend
        onCreate?.(response.data.data);
      } else {
        message.error(response.data?.message || "Failed to create the user.");
      }
    } catch (error) {
      console.error("Error creating Empire user:", error.response?.data || error);
 
      if (error.response?.status === 409) {
        message.error(
          error.response?.data?.message || "Username already exists."
        );
      } else if (error.response?.status === 400) {
        message.error(
          error.response?.data?.message || "Please check the submitted details."
        );
      } else if (error.response) {
        message.error(
          error.response.data?.message || "Server error while creating the user."
        );
      } else if (error.request) {
        message.error("Unable to reach the server. Please check your connection.");
      } else {
        message.error("An unexpected error occurred while creating the user.");
      }
    } finally {
      setSubmitting(false);
    }
  };
 
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#0ea5e9",
          borderRadius: 10,
          fontFamily: "'Plus Jakarta Sans', 'Segoe UI', system-ui, sans-serif",
          controlHeight: 42,
          colorBorder: "#dbe7f3",
        },
        components: {
          Segmented: { itemSelectedColor: "#0369a1" },
        },
      }}
    >
      {/* Optional font: add to index.html
          <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" /> */}
      <div className="min-h-screen px-4 py-6 sm:px-8 sm:py-10">
        <div className="mx-auto max-w-5xl">
          {/* Page title */}
          <div className="mb-6">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="!-ml-3 !mb-2 !text-slate-500"
            >
              Back to users
            </Button>
            <h1 className="text-2xl font-bold text-slate-800 sm:text-3xl">
              Add new user
            </h1>
            <p className="mt-1 text-sm text-slate-500 sm:text-base">
              Create a login and choose which modules this person can open.
            </p>
          </div>
 
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={handleFinish}
            scrollToFirstError
            className="space-y-5"
          >
            {/* Personal details */}
            <SectionCard
              icon={<UserOutlined />}
              title="Personal details"
              hint="How we identify and reach this user."
            >
              <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
                <Form.Item
                  label="Username"
                  name="username"
                  rules={[
                    { required: true, whitespace: true, message: "Enter a username" },
                    { min: 3, message: "Username must be at least 3 characters" },
                    {
                      pattern: /^[A-Za-z0-9._-]+$/,
                      message: "Use only letters, numbers, dots, dashes or underscores",
                    },
                  ]}
                >
                  <Input
                    prefix={<IdcardOutlined className="text-slate-400" />}
                    placeholder="e.g. aarav.sharma"
                    autoComplete="off"
                  />
                </Form.Item>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, whitespace: true, message: "Enter the name" }]}
                >
                  <Input
                    prefix={<UserOutlined className="text-slate-400" />}
                    placeholder="e.g. Aarav Sharma"
                  />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Enter the email address" },
                    { type: "email", message: "Enter a valid email address" },
                  ]}
                >
                  <Input prefix={<MailOutlined className="text-slate-400" />} placeholder="name@company.com" />
                </Form.Item>
                <Form.Item
                  label="Phone number"
                  name="phone"
                  rules={[
                    { required: true, message: "Enter the phone number" },
                    { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit mobile number" },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined className="text-slate-400" />}
                    addonBefore="+91"
                    maxLength={10}
                    inputMode="numeric"
                    placeholder="98765 43210"
                  />
                </Form.Item>
              </div>
            </SectionCard>
 
            {/* Role */}
            <SectionCard
              icon={<SafetyCertificateOutlined />}
              title="Role"
              hint="Admins get every module and can manage other users."
            >
              <Segmented
                block
                size="large"
                value={role}
                onChange={handleRoleChange}
                options={[
                  { label: "User", value: "user" },
                  { label: "Admin", value: "admin" },
                ]}
                className="max-w-xs !bg-sky-50"
              />
            </SectionCard>
 
            {/* Module access */}
            <SectionCard
              icon={<AppstoreOutlined />}
              title="Module access"
              hint={
                isAdmin
                  ? "Admins have access to all modules."
                  : `${selected.length} of ${visibleModules.length} selected · Dashboard is always included`
              }
              extra={
                <Checkbox
                  checked={allChecked}
                  indeterminate={selected.length > 0 && !allChecked}
                  disabled={isAdmin}
                  onChange={(e) => toggleAll(e.target.checked)}
                >
                  Select all
                </Checkbox>
              }
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {visibleModules.map((m) => {
                  const active = selected.includes(m.key);
                  const locked = REQUIRED_KEYS.includes(m.key);
                  const disabled = isAdmin || locked;
                  return (
                    <button
                      type="button"
                      key={m.key}
                      onClick={() => toggleModule(m.key)}
                      aria-pressed={active}
                      aria-disabled={disabled}
                      disabled={disabled}
                      title={locked ? "Dashboard is required and can't be removed" : undefined}
                      className={[
                        "flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300",
                        active
                          ? "border-sky-300 bg-sky-50 text-sky-800"
                          : "border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:bg-sky-50/50",
                        disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer",
                      ].join(" ")}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base ${
                          active ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {m.icon}
                      </span>
                      <span className="flex-1 text-sm font-medium">
                        {m.label}
                        {locked && !isAdmin && (
                          <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                            Required
                          </span>
                        )}
                      </span>
                      <Checkbox checked={active} disabled={disabled} tabIndex={-1} />
                    </button>
                  );
                })}
              </div>
            </SectionCard>
 
            {/* Password */}
            <SectionCard
              icon={<LockOutlined />}
              title="Password"
              hint="Use at least 8 characters with letters, a number and a symbol."
            >
              <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
                <div>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      { required: true, message: "Enter a password" },
                      { min: 8, message: "Password must be at least 8 characters" },
                    ]}
                    className="!mb-2"
                  >
                    <Input.Password
                      prefix={<LockOutlined className="text-slate-400" />}
                      placeholder="Create a password"
                      onChange={(e) => setPw(e.target.value)}
                    />
                  </Form.Item>
                  {/* Strength meter */}
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex flex-1 gap-1.5">
                      {[1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-colors ${
                            pw && i <= Math.max(strength, 1) ? meta.color : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <span className={`w-16 text-right text-xs font-medium ${meta.text}`}>
                      {pw ? meta.label : ""}
                    </span>
                  </div>
                </div>
 
                <Form.Item
                  label="Confirm password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Re-enter the password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) return Promise.resolve();
                        return Promise.reject(new Error("Passwords don't match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-slate-400" />}
                    placeholder="Re-enter the password"
                  />
                </Form.Item>
              </div>
            </SectionCard>
 
            {/* Actions */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button size="large" onClick={onBack} className="sm:min-w-28">
                Cancel
              </Button>
              <Button size="large" onClick={handleReset} className="sm:min-w-28">
                Clear form
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={submitting}
                className="sm:min-w-36"
              >
                Create user
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </ConfigProvider>
  );
}
 